<template>
  <DrawerModal
    v-if="isNativePlatform"
    :visible="promptOpen"
    test-id="bug-report-prompt-drawer"
    @close="dismissBugReportPrompt"
  >
    <template #header>
      <img
        :src="BugIcon"
        alt="Bug"
        class="drawer-icon"
      />
      <span class="drawer-title">Report a bug</span>
      <button
        type="button"
        class="drawer-close-button"
        aria-label="Close bug report prompt"
        @click="dismissBugReportPrompt"
      >
        <ion-icon :icon="closeIcon" />
      </button>
    </template>

    <div class="prompt-body">
      <p>
        Send a quick report with app details and an optional screenshot so we
        can investigate.
      </p>
      <BalanceButton
        buttonType="default"
        @click="openForm"
      >
        Report bug
      </BalanceButton>
      <div class="shake-section">
        <div class="divider"></div>
        <div class="shake-row">
          <div class="shake-title">{{ shakeToggleLabel }}</div>
          <ion-toggle
            :checked="shakeEnabled"
            @ionChange="handleShakeToggle"
          />
        </div>
      </div>
    </div>
  </DrawerModal>

  <DrawerModal
    v-if="isNativePlatform"
    :visible="formOpen"
    test-id="bug-report-form-drawer"
    @close="dismissBugReportNativeForm"
  >
    <template #header>
      <img
        :src="BugIcon"
        alt="Bug"
        class="drawer-icon"
      />
      <span class="drawer-title">Report bug</span>
      <button
        type="button"
        class="drawer-close-button"
        aria-label="Close bug report form"
        @click="dismissBugReportNativeForm"
      >
        <ion-icon :icon="closeIcon" />
      </button>
    </template>

    <form
      class="report-form"
      @submit.prevent="submitReport"
    >
      <label>
        <span>What happened?</span>
        <textarea
          v-model="message"
          rows="5"
          placeholder="Describe the issue you ran into."
          :maxlength="MAX_BUG_REPORT_TEXT_CHARS"
          required
        ></textarea>
      </label>

      <label>
        <span>What did you expect to happen?</span>
        <textarea
          v-model="expectedBehavior"
          rows="3"
          placeholder="Optional, but helpful."
          :maxlength="MAX_BUG_REPORT_TEXT_CHARS"
        ></textarea>
      </label>

      <div
        v-if="screenshotDataUrl"
        class="screenshot-section"
      >
        <div class="screenshot-header">
          <div>
            <div class="screenshot-title">Include screenshot</div>
          </div>
          <ion-toggle
            :checked="includeScreenshot"
            @ionChange="includeScreenshot = $event.detail.checked"
          />
        </div>
        <img
          v-if="includeScreenshot"
          :src="screenshotDataUrl"
          alt="Bug report screenshot preview"
          class="screenshot-preview"
        />
      </div>

      <p
        v-if="errorMessage"
        class="error-message"
      >
        {{ errorMessage }}
      </p>

      <BalanceButton
        buttonType="default"
        :loading="submitting"
        :disabled="!message.trim()"
        @click="submitReport"
      >
        Send report
      </BalanceButton>
    </form>
  </DrawerModal>

  <div
    v-if="!isNativePlatform && formOpen"
    class="web-bug-report-backdrop"
    @click.self="dismissBugReportWebForm"
  >
    <section
      class="web-bug-report-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="web-bug-report-title"
    >
      <div class="web-bug-report-header">
        <h2
          id="web-bug-report-title"
          class="web-bug-report-title"
        >
          What happened?
        </h2>
        <button
          type="button"
          class="web-close-button"
          aria-label="Close bug report form"
          @click="dismissBugReportWebForm"
        >
          <ion-icon :icon="closeIcon" />
        </button>
      </div>

      <textarea
        ref="webMessageInput"
        v-model="message"
        class="web-bug-report-textarea"
        :maxlength="MAX_BUG_REPORT_TEXT_CHARS"
        placeholder="Tell us about the issue you encountered"
        aria-label="Tell us about the issue you encountered"
        @keydown.esc="dismissBugReportWebForm"
      ></textarea>

      <p
        v-if="errorMessage"
        class="error-message"
      >
        {{ errorMessage }}
      </p>

      <div class="web-bug-report-footer">
        <span class="web-character-count">
          {{ message.length }} / {{ MAX_BUG_REPORT_TEXT_CHARS }} characters used
        </span>
        <button
          type="button"
          class="web-send-button"
          :disabled="submitting || !message.trim()"
          @click="submitReport"
        >
          <ion-spinner
            v-if="submitting"
            name="crescent"
            color="light"
          />
          <span v-else>Send</span>
        </button>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { IonIcon, IonSpinner, IonToggle } from '@ionic/vue'
import { Capacitor, type PluginListenerHandle } from '@capacitor/core'
import {
  Keyboard,
  KeyboardResize,
  type KeyboardInfo,
} from '@capacitor/keyboard'
import { Screenshot } from 'capacitor-screenshot'
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { close as closeIcon } from 'ionicons/icons'
import { useProfilesStore } from '@/stores/profile'
import { useMessagesStore } from '@/stores/messages'
import { usePostHog } from '@/composables/usePostHog'
import { useShakeToReportBug } from '@/composables/useShakeToReportBug'
import { AnalyticsEvent } from '@/constants/analyticsEvents'
import {
  createBugReport,
  MAX_BUG_REPORT_TEXT_CHARS,
  uploadBugReportScreenshot,
} from '@/services/bugReports'
import type { BugReportScreenshotUpload } from '@/services/bugReports'
import DrawerModal from '@/components/DrawerModal.vue'
import BalanceButton from '@/components/BalanceButton.vue'
import BugIcon from '@/assets/img/category-icons/bug.svg'

const promptOpen = ref(false)
const formOpen = ref(false)
const source = ref<'shake' | 'manual'>('manual')
const message = ref('')
const expectedBehavior = ref('')
const screenshotDataUrl = ref('')
const includeScreenshot = ref(true)
const submitting = ref(false)
const errorMessage = ref('')
const webMessageInput = ref<HTMLTextAreaElement>()
const BUG_REPORT_KEYBOARD_OFFSET_VAR = '--bug-report-keyboard-offset'

let keyboardWillShowListener: PluginListenerHandle | undefined
let keyboardWillHideListener: PluginListenerHandle | undefined
let previousKeyboardResizeMode: KeyboardResize | undefined

const route = useRoute()
const profileStore = useProfilesStore()
const messagesStore = useMessagesStore()
const { enabled: shakeEnabled, setShakeToReportBugEnabled } =
  useShakeToReportBug()
const { track, posthog } = usePostHog()
const { activeProfile } = storeToRefs(profileStore)

const routeName = computed(() =>
  typeof route.name === 'string' ? route.name : undefined,
)
const isNativePlatform = computed(() => Capacitor.isNativePlatform())
const isIOSNativePlatform = computed(
  () => isNativePlatform.value && Capacitor.getPlatform() === 'ios',
)
const shakeToggleLabel = computed(() =>
  Capacitor.getPlatform() === 'ios'
    ? 'Shake iPhone to report a bug'
    : 'Shake phone to report a bug',
)

async function captureScreenshot() {
  try {
    const result = await Screenshot.take()
    if (result.base64) {
      screenshotDataUrl.value = `data:image/png;base64,${result.base64}`
      includeScreenshot.value = true
    }
  } catch (error) {
    screenshotDataUrl.value = ''
    includeScreenshot.value = false
    console.warn('Screenshot capture failed:', error)
  }
}

function resetBugReportKeyboardOffset() {
  document.documentElement.style.removeProperty(BUG_REPORT_KEYBOARD_OFFSET_VAR)
}

function handleKeyboardWillShow(info: KeyboardInfo) {
  if (!formOpen.value || !isIOSNativePlatform.value) return
  document.documentElement.style.setProperty(
    BUG_REPORT_KEYBOARD_OFFSET_VAR,
    `${info.keyboardHeight}px`,
  )
}

function handleKeyboardWillHide() {
  resetBugReportKeyboardOffset()
}

async function useBugReportKeyboardLayout() {
  if (!isIOSNativePlatform.value) return

  try {
    previousKeyboardResizeMode = (await Keyboard.getResizeMode())
      .mode as KeyboardResize
    await Keyboard.setResizeMode({ mode: KeyboardResize.None })
  } catch (error) {
    console.warn('Failed to prepare bug report keyboard layout:', error)
  }
}

async function restoreAppKeyboardLayout() {
  resetBugReportKeyboardOffset()

  if (!isIOSNativePlatform.value) return

  try {
    await Keyboard.setResizeMode({
      mode: previousKeyboardResizeMode || KeyboardResize.Body,
    })
    previousKeyboardResizeMode = undefined
  } catch (error) {
    console.warn('Failed to restore keyboard layout:', error)
  }
}

async function handleOpenBugReport(event: Event) {
  const detail = (event as CustomEvent<{ source?: 'shake' | 'manual' }>).detail
  source.value = detail?.source || 'manual'
  promptOpen.value = false
  formOpen.value = false
  message.value = ''
  expectedBehavior.value = ''
  screenshotDataUrl.value = ''
  includeScreenshot.value = false
  errorMessage.value = ''
  track(AnalyticsEvent.BUG_REPORT_PROMPT_OPENED, { source: source.value })

  if (!isNativePlatform.value) {
    formOpen.value = true
    track(AnalyticsEvent.BUG_REPORT_STARTED, { source: source.value })
    await nextTick()
    webMessageInput.value?.focus()
    return
  }

  await captureScreenshot()
  promptOpen.value = true
}

async function openForm() {
  promptOpen.value = false
  await useBugReportKeyboardLayout()
  formOpen.value = true
  track(AnalyticsEvent.BUG_REPORT_STARTED, { source: source.value })
}

function dismissBugReportPrompt() {
  track(AnalyticsEvent.BUG_REPORT_CANCELLED, { stage: 'prompt' })
  promptOpen.value = false
}

async function dismissBugReportNativeForm() {
  track(AnalyticsEvent.BUG_REPORT_CANCELLED, { stage: 'native_form' })
  formOpen.value = false
  await Keyboard.hide().catch(() => undefined)
  await restoreAppKeyboardLayout()
}

function dismissBugReportWebForm() {
  track(AnalyticsEvent.BUG_REPORT_CANCELLED, { stage: 'web_form' })
  formOpen.value = false
}

async function handleShakeToggle(event: CustomEvent<{ checked: boolean }>) {
  await setShakeToReportBugEnabled(event.detail.checked)
}

async function submitReport() {
  if (submitting.value || !message.value.trim()) return

  submitting.value = true
  errorMessage.value = ''

  try {
    let uploadedScreenshot: BugReportScreenshotUpload | undefined

    if (includeScreenshot.value && screenshotDataUrl.value) {
      try {
        uploadedScreenshot = await uploadBugReportScreenshot(
          screenshotDataUrl.value,
        )
      } catch (error) {
        console.warn('Bug report screenshot upload failed:', error)
      }
    }

    const response = await createBugReport({
      message: message.value,
      expectedBehavior: expectedBehavior.value,
      source: source.value,
      profileId: activeProfile.value?.profileId,
      routePath: route.fullPath,
      routeName: routeName.value,
      appVersion: import.meta.env.VITE_APP_VERSION,
      platform: Capacitor.getPlatform(),
      devicePlatform: window.navigator?.platform,
      userAgent: window.navigator?.userAgent,
      posthogDistinctId: posthog.get_distinct_id(),
      posthogSessionId: posthog.get_session_id(),
      screenshot: uploadedScreenshot
        ? {
            included: true,
            ...uploadedScreenshot,
          }
        : { included: false },
    })

    track(AnalyticsEvent.BUG_REPORT_SUBMITTED, {
      source: source.value,
      reportId: response.reportId,
      screenshotIncluded: Boolean(uploadedScreenshot),
    })

    formOpen.value = false
    await restoreAppKeyboardLayout()
    messagesStore.messages.push({
      message:
        "Thanks for the report. You're helping make Balance better for everyone 😊",
      duration: 3000,
    })
  } catch (error) {
    console.error('Bug report submission failed:', error)
    track(AnalyticsEvent.BUG_REPORT_SUBMIT_FAILED, {
      source: source.value,
      error: String(error),
    })
    errorMessage.value = 'Something went wrong sending your report.'
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  window.addEventListener('balance:open-bug-report', handleOpenBugReport)

  if (isIOSNativePlatform.value) {
    Keyboard.addListener('keyboardWillShow', handleKeyboardWillShow)
      .then((listener) => {
        keyboardWillShowListener = listener
      })
      .catch((error) => {
        console.warn('Failed to listen for keyboard show:', error)
      })
    Keyboard.addListener('keyboardWillHide', handleKeyboardWillHide)
      .then((listener) => {
        keyboardWillHideListener = listener
      })
      .catch((error) => {
        console.warn('Failed to listen for keyboard hide:', error)
      })
  }
})

onUnmounted(() => {
  window.removeEventListener('balance:open-bug-report', handleOpenBugReport)
  keyboardWillShowListener?.remove()
  keyboardWillHideListener?.remove()
  restoreAppKeyboardLayout()
})
</script>

<style scoped>
.drawer-icon {
  width: 24px;
  height: 24px;
}

.drawer-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--ion-color-gray8);
  flex: 1;
  text-align: left;
}

.drawer-close-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 18px;
  background: var(--ion-color-gray1);
  color: var(--ion-color-gray8);
  padding: 0;
}

.drawer-close-button ion-icon {
  width: 22px;
  height: 22px;
}

.prompt-body {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
  min-width: 0;
  overflow-x: hidden;
}

.prompt-body p {
  margin: 0;
  color: var(--ion-color-gray6);
  font-size: 16px;
  line-height: 24px;
}

.shake-section {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  min-width: 0;
}

.divider {
  height: 1px;
  background: var(--ion-color-gray2);
}

.shake-row,
.screenshot-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;
}

.shake-title,
.screenshot-title {
  color: var(--ion-color-gray8);
  font-size: 16px;
  font-weight: 600;
}

.report-form {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
  min-width: 0;
  overflow-x: hidden;
}

.report-form label {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: var(--ion-color-gray8);
  font-size: 15px;
  font-weight: 700;
}

.report-form textarea {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  resize: vertical;
  border: 1px solid var(--ion-color-gray2);
  border-radius: 8px;
  background: var(--ion-color-gray0);
  color: var(--ion-color-gray8);
  padding: 12px;
  font: inherit;
  font-weight: 400;
  line-height: 22px;
}

.report-form textarea:focus {
  border-color: var(--ion-color-primary);
  outline: none;
  box-shadow: inset 0 0 0 2px var(--ion-color-primary);
}

.screenshot-section {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  min-width: 0;
  border: 1px solid var(--ion-color-gray2);
  border-radius: 8px;
  padding: 12px;
}

.screenshot-preview {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  max-height: 360px;
  object-fit: contain;
  border-radius: 8px;
  background: var(--ion-color-gray1);
}

:global(.drawer-modal[data-testid='bug-report-form-drawer']) {
  display: flex;
  flex-direction: column;
  bottom: var(--bug-report-keyboard-offset, 0px);
  max-height: calc(
    100vh - var(--bug-report-keyboard-offset, 0px) - env(safe-area-inset-top) -
      12px
  );
  overflow: hidden;
}

@supports (height: 100dvh) {
  :global(.drawer-modal[data-testid='bug-report-form-drawer']) {
    max-height: calc(
      100dvh - var(--bug-report-keyboard-offset, 0px) - env(safe-area-inset-top) -
        12px
    );
  }
}

:global(.drawer-modal[data-testid='bug-report-form-drawer'] .drawer-content) {
  flex: 1;
  min-height: 0;
  max-height: none;
  overscroll-behavior: contain;
}

.error-message {
  margin: 0;
  color: var(--ion-color-danger);
  font-size: 14px;
}

.web-bug-report-backdrop {
  position: fixed;
  inset: 0;
  /* Above DrawerModal mask (999); below app-level ion-modal overlays (~10k+) */
  z-index: 5000;
  display: grid;
  place-items: center;
  box-sizing: border-box;
  padding: 24px;
  background: rgba(var(--ion-color-dark-rgb), 0.38);
}

.web-bug-report-dialog {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: min(100%, 560px);
  max-height: calc(100vh - 48px);
  padding: 20px;
  overflow: hidden;
  border: 1px solid var(--ion-color-gray2);
  border-radius: 16px;
  background: var(--ion-color-gray0);
  box-shadow: 0 24px 64px rgba(var(--ion-color-dark-rgb), 0.24);
}

.web-bug-report-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;
}

.web-bug-report-title {
  margin: 0;
  color: var(--ion-color-gray8);
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
}

.web-close-button {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border: 1px solid var(--ion-color-gray3);
  border-radius: 8px;
  background: var(--ion-color-gray0);
  color: var(--ion-color-gray8);
  padding: 0;
  cursor: pointer;
}

.web-close-button:hover,
.web-close-button:focus-visible {
  background: var(--ion-color-gray1);
}

.web-close-button ion-icon {
  width: 22px;
  height: 22px;
}

.web-bug-report-textarea {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-height: 260px;
  resize: vertical;
  border: 1px solid var(--ion-color-gray3);
  border-radius: 8px;
  background: var(--ion-color-gray0);
  color: var(--ion-color-gray8);
  padding: 14px 16px;
  font: inherit;
  font-size: 15px;
  font-weight: 400;
  line-height: 22px;
}

.web-bug-report-textarea::placeholder {
  color: var(--ion-color-gray5);
}

.web-bug-report-textarea:focus {
  border-color: var(--ion-color-primary);
  outline: none;
}

.web-bug-report-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;
}

.web-character-count {
  color: var(--ion-color-gray5);
  font-size: 14px;
  line-height: 20px;
}

.web-send-button {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  min-width: 72px;
  height: 40px;
  border: 0;
  border-radius: 20px;
  background: var(--ion-color-gray8);
  color: var(--ion-color-gray0);
  padding: 0 18px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
}

.web-send-button:disabled {
  cursor: default;
  opacity: 0.42;
}

.web-send-button ion-spinner {
  width: 18px;
  height: 18px;
}

@media (max-width: 640px) {
  .web-bug-report-backdrop {
    align-items: end;
    padding: 0;
  }

  .web-bug-report-dialog {
    width: 100%;
    max-height: 82vh;
    border-right: 0;
    border-bottom: 0;
    border-left: 0;
    border-radius: 24px 24px 0 0;
  }

  .web-bug-report-textarea {
    min-height: 220px;
  }
}
</style>
